import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title, DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Sortable from 'sortablejs';
import {
  CreatePartnerPageGQL, UpdatePartnerPageGQL, GetSiteGQL, GetArticlesGQL, GetArticleGQL,
  GetPartnerPageVersionsGQL, RevertPartnerPageGQL, PartnerPageVersion, GetProfilesGQL
} from '../../../graphql/graphql';
import { ToastService } from '../../../shared/toast/toast.service';
import { UploadService } from '../../../shared/upload/upload.service';
import slugify from 'slugify';

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  standalone: false
})
export class ArticleComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('blocksContainer') blocksContainer?: ElementRef<HTMLElement>;
  private sortableInstance: Sortable | null = null;
  private sortableEl: HTMLElement | null = null;
  articleForm: FormGroup;
  isEdit = false;
  articleId: string | null = null;
  siteId: string = '';
  siteUrl: string = '';

  showVersionPanel = false;
  versions: PartnerPageVersion[] = [];
  currentVersionNumber: number | null = null;
  previewVersion: PartnerPageVersion | null = null;
  showRevertModal = false;
  versionToRevert: PartnerPageVersion | null = null;

  profiles: any[] = [];
  siteName: string = '';

  blockTypes = [
    { value: 'text', label: 'Text' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private createPartnerPageGQL: CreatePartnerPageGQL,
    private updatePartnerPageGQL: UpdatePartnerPageGQL,
    private getSiteGQL: GetSiteGQL,
    private getArticlesGQL: GetArticlesGQL,
    private getArticleGQL: GetArticleGQL,
    private getPartnerPageVersionsGQL: GetPartnerPageVersionsGQL,
    private revertPartnerPageGQL: RevertPartnerPageGQL,
    private getProfilesGQL: GetProfilesGQL,
    private toast: ToastService,
    private titleService: Title,
    private uploadService: UploadService,
    private sanitizer: DomSanitizer
  ) {
    this.articleForm = this.fb.group({
      title: ['', Validators.required],
      seoTitle: ['', Validators.required],
      byline: [''],
      slug: ['', Validators.required],
      author: ['', Validators.required],
      authorTitle: [''],
      articleDate: ['', Validators.required],
      thumbnail: this.fb.group({
        lg: [''],
        md: [''],
        sm: [''],
        alt: ['']
      }),
      published: [false],
      description: ['', Validators.required],
      blocks: this.fb.array([])
    });
  }

  get blocks(): FormArray {
    return this.articleForm.get('blocks') as FormArray;
  }

  ngOnInit() {
    this.articleId = this.route.snapshot.params['id'];
    this.isEdit = !!this.articleId;
    this.siteUrl = this.route.parent?.snapshot.params['url'] ||
                   this.route.parent?.parent?.snapshot.params['url'] || '';
    this.loadSiteData();
  }

  loadSiteData() {
    if (this.siteUrl) {
      this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
        if (result.data?.site) {
          this.siteId = result.data.site._id!;
          this.siteName = result.data.site.client?.name || '';
          this.loadProfiles();
          if (this.isEdit) {
            this.loadArticleData();
            this.loadVersions();
          } else {
            this.setupSlugGeneration();
          }
        }
      });
    }
  }

  loadArticleData() {
    if (this.siteId && this.articleId) {
      this.getArticleGQL.watch({ variables: { id: this.articleId } }).valueChanges.subscribe(result => {
        if (result.data?.partnerPage) {
          const article = result.data.partnerPage;
          this.articleForm.patchValue({
            title: article.title,
            seoTitle: article.seoTitle || '',
            byline: article.byline || '',
            slug: article.slug,
            author: article.author || '',
            authorTitle: article.authorTitle || '',
            articleDate: article.articleDate ? article.articleDate.substring(0, 10) : '',
            thumbnail: article.thumbnail?.lg ? {
              lg: article.thumbnail.lg,
              md: article.thumbnail.md,
              sm: article.thumbnail.sm,
              alt: article.thumbnail.alt || ''
            } : { lg: '', md: '', sm: '', alt: '' },
            published: article.published,
            description: article.description || ''
          });
          this.titleService.setTitle(`Articles | ${article.title} | Saltus Partners`);

          if (article.content?.main?.blocks) {
            this.blocks.clear();
            article.content.main.blocks.forEach((block: any) => {
              this.blocks.push(this.createBlockGroup(block));
            });
          }
          this.setupSlugGeneration();
        }
      });
    }
  }

  loadVersions() {
    if (this.articleId) {
      this.getPartnerPageVersionsGQL.watch({ variables: { pageId: this.articleId }, fetchPolicy: 'network-only' }).valueChanges.subscribe(result => {
        if (result.data?.partnerPageVersions) {
          this.versions = result.data.partnerPageVersions as PartnerPageVersion[];
          if (this.versions.length > 0) {
            this.currentVersionNumber = this.versions[0].versionNumber;
          }
        }
      });
    }
  }

  toggleVersionPanel() {
    this.showVersionPanel = !this.showVersionPanel;
    if (this.showVersionPanel && this.versions.length === 0) {
      this.loadVersions();
    }
  }

  previewVersionSnapshot(version: PartnerPageVersion) {
    this.previewVersion = version;
  }

  closePreview() {
    this.previewVersion = null;
  }

  revertToVersion(version: PartnerPageVersion) {
    if (!this.articleId) return;
    this.versionToRevert = version;
    this.showRevertModal = true;
  }

  confirmRevert() {
    const version = this.versionToRevert;
    if (!version || !this.articleId) return;
    this.showRevertModal = false;
    this.versionToRevert = null;

    this.revertPartnerPageGQL.mutate({
      variables: { id: this.articleId, versionNumber: version.versionNumber },
      refetchQueries: [
        { query: this.getArticleGQL.document, variables: { id: this.articleId } },
        { query: this.getPartnerPageVersionsGQL.document, variables: { pageId: this.articleId } }
      ]
    }).subscribe({
      next: () => {
        this.toast.success(`Reverted to version ${version.versionNumber}`);
        this.previewVersion = null;
        this.loadArticleData();
        this.loadVersions();
      },
      error: () => this.toast.error('Error reverting article')
    });
  }

  cancelRevert() {
    this.showRevertModal = false;
    this.versionToRevert = null;
  }

  getPreviewBlocks(): any[] {
    return this.previewVersion?.snapshot?.content?.main?.blocks || [];
  }

  loadProfiles() {
    if (this.siteId) {
      this.getProfilesGQL.watch({ variables: { siteId: this.siteId } }).valueChanges.subscribe(result => {
        if (result.data?.profiles) {
          this.profiles = [...result.data.profiles].sort((a, b) => {
            const surnameA = (a.surname || '').toLowerCase();
            const surnameB = (b.surname || '').toLowerCase();
            return surnameA.localeCompare(surnameB);
          });
        }
      });
    }
  }

  createBlockGroup(data?: any): FormGroup {
    const type = data?.type || 'text';
    const isImage = type === 'image';
    const isText = type === 'text';
    const isVideo = type === 'video';
    return this.fb.group({
      type: [type],
      text: [data?.text || '', isText ? Validators.required : []],
      title: [data?.title || ''],
      videoUrl: [data?.videoUrl || data?.video || '', isVideo ? Validators.required : []],
      image: this.fb.group({
        lg: [data?.image?.lg || '', isImage ? Validators.required : []],
        md: [data?.image?.md || ''],
        sm: [data?.image?.sm || ''],
        alt: [data?.image?.alt || '', isImage ? Validators.required : []]
      })
    });
  }

  async uploadThumbnail() {
    const path = `articles/${this.articleId || 'new'}/`;
    const results = await this.uploadService.pick({
      path,
      imageMax: [1080, 756],
      transformations: { crop: { aspectRatio: 1080 / 756, force: true } },
    });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      this.articleForm.get('thumbnail')?.patchValue({
        lg: `${base}/resize=width:1080,height:756,fit:crop/output=quality:80/${handle}`,
        md: `${base}/resize=width:540/output=quality:80/${handle}`,
        sm: `${base}/resize=width:300/output=quality:80/${handle}`,
      });
    }
  }

  async uploadBlockImage(blockIndex: number) {
    const path = `articles/${this.articleId || 'new'}/blocks/`;
    const results = await this.uploadService.pick({ path, imageMax: [1302, 100000] });
    if (results.length) {
      const handle = results[0].url.split('/').pop();
      const base = 'https://cdn.filestackcontent.com';
      const imageGroup = this.blocks.at(blockIndex).get('image');
      imageGroup?.patchValue({
        lg: `${base}/resize=width:1302/output=quality:80/${handle}`,
        md: `${base}/resize=width:702/output=quality:80/${handle}`,
        sm: `${base}/resize=width:400/output=quality:80/${handle}`,
      });
    }
  }

  addBlock(type: string = 'text') {
    this.blocks.push(this.createBlockGroup({ type }));
  }

  removeBlock(index: number) {
    this.blocks.removeAt(index);
  }

  ngAfterViewChecked() {
    const el = this.blocksContainer?.nativeElement;
    if (el && el !== this.sortableEl) {
      this.destroySortable();
      this.sortableEl = el;
      this.sortableInstance = Sortable.create(el, {
        animation: 150,
        handle: '.drag-handle',
        ghostClass: 'sortable-ghost',
        onEnd: (evt) => {
          if (evt.oldIndex !== evt.newIndex) {
            const ctrl = this.blocks.at(evt.oldIndex!);
            this.blocks.removeAt(evt.oldIndex!);
            this.blocks.insert(evt.newIndex!, ctrl);
          }
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroySortable();
  }

  private destroySortable() {
    this.sortableInstance?.destroy();
    this.sortableInstance = null;
    this.sortableEl = null;
  }

  onSubmit() {
    if (this.articleForm.invalid) {
      this.articleForm.markAllAsTouched();
      return;
    }

    if (this.siteId) {
      const f = this.articleForm.value;
      const blocks = f.blocks.map((block: any) => {
        const b: any = { type: block.type };
        if (block.text) b.text = block.text;
        if (block.title) b.title = block.title;
        if (block.videoUrl) b.video = block.videoUrl;
        if (block.image?.lg || block.image?.md) b.image = block.image;
        return b;
      });

      const input: any = {
        name: f.title,
        title: f.title,
        seoTitle: f.seoTitle || '',
        byline: f.byline || null,
        description: f.description || '',
        slug: f.slug,
        published: f.published,
        site: this.siteId,
        type: 'article',
        author: f.author || null,
        authorTitle: f.authorTitle || null,
        articleDate: f.articleDate ? new Date(f.articleDate).toISOString() : null,
        thumbnail: f.thumbnail?.lg ? { lg: f.thumbnail.lg, md: f.thumbnail.md, sm: f.thumbnail.sm, alt: f.thumbnail.alt || '' } : null,
        content: {
          layout: 'article',
          title: f.title,
          tagline: '',
          main: { blocks },
          sidebar: { blocks: [] }
        }
      };

      const refetch = { refetchQueries: [{ query: this.getArticlesGQL.document, variables: { siteId: this.siteId, type: 'article' } }] };

      if (this.isEdit && this.articleId) {
        this.updatePartnerPageGQL.mutate({ variables: { id: this.articleId, updatePartnerPageInput: input }, ...refetch }).subscribe({
          next: () => {
            this.toast.success('Article updated');
            this.loadVersions();
          },
          error: () => this.toast.error('Error updating article')
        });
      } else {
        this.createPartnerPageGQL.mutate({ variables: { createPartnerPageInput: input }, ...refetch }).subscribe({
          next: (result) => {
            this.toast.success('Article created');
            if (result.data?.createPartnerPage?._id) {
              this.articleId = result.data.createPartnerPage._id;
              this.isEdit = true;
              this.router.navigate(
                ['sites', this.siteUrl, 'manage', 'articles', 'article', this.articleId],
                { replaceUrl: true }
              );
              this.loadVersions();
            }
          },
          error: () => this.toast.error('Error creating article')
        });
      }
    }
  }

  setupSlugGeneration() {
    this.articleForm.get('title')?.valueChanges.subscribe(title => {
      if (title && !this.isEdit) {
        this.articleForm.patchValue({ slug: slugify(title, { lower: true, strict: true }) }, { emitEvent: false });
      }
    });
    this.articleForm.get('author')?.valueChanges.subscribe(authorName => {
      const profile = this.profiles.find(p => p.name === authorName);
      this.articleForm.patchValue({ authorTitle: profile?.title || '' }, { emitEvent: false });
    });
  }

  getVideoEmbedUrl(url: string): SafeResourceUrl | null {
    if (!url?.trim()) return null;
    try {
      const parsed = new URL(url.trim());
      if (/vimeo\.com/.test(parsed.hostname)) {
        const id = parsed.pathname.match(/(?:video\/)?(\d+)/)?.[1];
        return id ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${id}`) : null;
      }
      if (/(?:youtube\.com|youtu\.be)/.test(parsed.hostname)) {
        let id = parsed.searchParams.get('v');
        if (!id) id = parsed.pathname.match(/^\/(embed|shorts|live|v)\/([\w-]+)/)?.[2] || null;
        if (!id && parsed.hostname === 'youtu.be') id = parsed.pathname.slice(1).split('/')[0];
        return id ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${id}`) : null;
      }
    } catch {}
    return null;
  }

  onCancel() {
    if (this.isEdit) {
      this.router.navigate(['../../'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  }
}
